
import React, { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useFavorites } from "@/hooks/useFavorites";
import { Star } from "lucide-react";
import { puttingSituations } from "@/data/puttingSituations";
import { useTranslation } from "react-i18next";

interface PuttingFavoritesProps {
  onSelectSituation: (id: number) => void;
  refreshAfterToast?: () => void;
}

const PuttingFavorites: React.FC<PuttingFavoritesProps> = ({ onSelectSituation, refreshAfterToast }) => {
  const { favorites, isLoading, refreshFavorites, removeFavorite } = useFavorites();
  const { t } = useTranslation('training');
  
  // Always refresh favorites when component mounts
  useEffect(() => {
    console.log("PuttingFavorites: Refreshing favorites on mount");
    refreshFavorites();
  }, [refreshFavorites]);
  
  // Filter to only get putting favorites
  const puttingFavorites = favorites.filter(fav => fav.test_type === "putting");
  
  // Get situation details by ID
  const getSituationDetails = (testId: string) => {
    const id = parseInt(testId, 10);
    return puttingSituations.find(situation => situation.id === id);
  };
  
  if (isLoading) {
    return (
      <Card className="mb-8">
        <CardHeader className="pb-3">
          <CardTitle className="text-xl flex items-center">
            <Star className="h-5 w-5 mr-2 text-amber-500 fill-amber-500" />
            {t('putting.favorites.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500">{t('putting.favorites.loading')}</p>
        </CardContent>
      </Card>
    );
  }
  
  if (puttingFavorites.length === 0) {
    return (
      <Card className="mb-8">
        <CardHeader className="pb-3">
          <CardTitle className="text-xl flex items-center">
            <Star className="h-5 w-5 mr-2 text-amber-500 fill-amber-500" />
            {t('putting.favorites.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500">{t('putting.favorites.noFavorites')}</p>
          <p className="text-gray-500 text-sm mt-2">
            {t('putting.favorites.addToFavorites')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-8">
      <CardHeader className="pb-3">
        <CardTitle className="text-xl flex items-center">
          <Star className="h-5 w-5 mr-2 text-amber-500 fill-amber-500" />
          {t('putting.favorites.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {puttingFavorites.map((favorite) => {
            const situationDetails = getSituationDetails(favorite.test_id);
            if (!situationDetails) return null;
            
            return (
              <div 
                key={favorite.id}
                className="cursor-pointer border rounded-md p-4 hover:bg-gray-50 transition-colors flex flex-col space-y-2 relative"
                onClick={() => {
                  onSelectSituation(situationDetails.id);
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFavorite(favorite.id);
                    if (refreshAfterToast) {
                      refreshAfterToast();
                    }
                  }}
                  className="absolute top-2 right-2 p-1 hover:bg-gray-200 rounded-full transition-colors"
                  aria-label={t('putting.favorites.removeFromFavorites')}
                >
                  <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
                </button>
                <div className="flex items-center w-full pr-8">
                  <img 
                    src={situationDetails.image} 
                    alt={situationDetails.name} 
                    className="w-12 h-12 object-cover rounded-md mr-3"
                  />
                  <span className="font-medium">{situationDetails.name}</span>
                </div>
                <p className="text-xs text-gray-500 w-full line-clamp-2">{situationDetails.description}</p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default PuttingFavorites;
